import axios from "axios";
import { createStore } from "vuex";
import io from "socket.io-client";
import router from "@/router";

const rootAPI = process.env.VUE_APP_ROOT_API;
const rootSocket = process.env.VUE_APP_SOCKET_HOST;
const selectedLanguage = process.env.VUE_APP_I18N_FALLBACK_LOCALE;
const socket: any = {};

// Auth persistence: when "remember me" is on we keep the session in
// localStorage (survives browser restart); otherwise we use sessionStorage
// so the token is dropped when the browser is closed. Reads fall back across
// both stores so a page refresh restores whichever one holds the session.
function readStored(key: string): string | null {
  return localStorage.getItem(key) || sessionStorage.getItem(key);
}

function writeAuth(persist: boolean, entries: Record<string, string>): void {
  const primary = persist ? localStorage : sessionStorage;
  const secondary = persist ? sessionStorage : localStorage;
  Object.keys(entries).forEach((key) => {
    primary.setItem(key, entries[key]);
    secondary.removeItem(key);
  });
}

function clearAuth(keys: string[]): void {
  keys.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

export default createStore({
  state: {
    token: readStored("access_token") || null,
    user: readStored("user") || "",
    card: readStored("ActiveCard") || 0,
    per: readStored("permis") || 0,

    selectedLanguage:
      localStorage.getItem("selected_language") || selectedLanguage,
    rootAPI: rootAPI,
    rootSocket: rootSocket,
    socket: socket,
    cancellationReasons: [],
  },
  getters: {
    loggedIn(state) {
      return state.token ? true : false;
    },
    getUser(state) {
      if (state.token != null) return JSON.parse(state.user);
      else return "";
    },
    getActiveCard(state) {
      console.log("joines get active card");
      return state.card;
    },
    getPer(state) {
      console.log("joines get permision");
      return state.per;
    },
    getToken(state) {
      return state.token;
    },
    getRootAPI(state) {
      return state.rootAPI;
    },

    selectedLanguage(state) {
      return state.selectedLanguage;
    },
    getCancellationReasons(state) {
      return state.cancellationReasons;
    },
  },
  mutations: {
    retrieveToken(state, data) {
      state.token = data.token;
      state.user = data.user;
    },
    setCancellationReasons(state, value) {
      state.cancellationReasons = value;
    },
    destroyToken(state) {
      state.token = null;
      state.user = "";
      state.card = 0;
      state.per = 0;

      clearAuth(["access_token", "ActiveCard", "user", "permis"]);
      console.log("done");
    },
    disconnectSocket(state) {
      state.socket.disconnect();
      state.socket = {};
    },
    setLanguage(state, language) {
      state.selectedLanguage = language;
    },

    setPer(state, permis) {
      console.log("set per");
      state.per = permis;
      localStorage.setItem("permis", permis);
    },
  },
  actions: {
    retrieveToken(context, data) {
      if (context.getters.loggedIn) {
        // A stale session is still present; clear it before re-authenticating
        // so we never resolve undefined and never keep a dead token around.
        context.commit("destroyToken");
      }
      return new Promise((resolve, reject) => {
        axios
          .post(rootAPI + "login", {
            username: data.username,
            password: data.password,
          })
          .then((response) => {
            const token = response.data.data.accessToken;
            const user = response.data.data;
            context.commit("retrieveToken", {
              token: token,
              user: JSON.stringify(user),
            });

            writeAuth(Boolean(data.saveToCookies), {
              user: JSON.stringify(user),
              access_token: token,
            });
            localStorage.setItem("selected_language", user.language);
            context.commit("setLanguage", user.language);
            axios.defaults.headers.common["Authorization"] = "Bearer " + token;
            resolve(response);
          })
          .catch((error) => {
            console.log(error);
            if (error.message == "Network Error" && !error.response)
              reject("Please check your Internet connection");
            else if (error.response) reject(error.response.data.message);
            else reject(error);
          });
      });
    },
    updateInfo(context, data) {
      if (context.getters.loggedIn) {
        const user = context.getters.getUser;
        user.full_name = data.full_name;
        user.hotel_name = data.hotel_name;
        localStorage.setItem("user", JSON.stringify(user));
      }
    },
    destroyToken(context) {
      if (context.getters.loggedIn) {
        return new Promise((resolve, reject) => {
          axios
            .put(rootAPI + "logout")
            .then((response) => {
              context.commit("destroyToken");
              console.log("done 2");
              resolve(response);
            })
            .catch((error) => {
              if (error.message == "Network Error" && !error.response) {
                reject("Please check your Internet connection");
              }
              reject(error.response.data.message);
            });
        });
      }
    },
    getOrders(context, type) {
      // get all the orders from the API
      // put them into the state
      if (context.getters.loggedIn) {
        return new Promise((resolve, reject) => {
          axios
            .get(rootAPI + "booking", {
              params: { time: type },
            })
            .then((response) => {
              resolve(response.data.data);
            })
            .catch((error) => {
              if (error.message == "Network Error" && !error.response) {
                reject("Please check your Internet connection");
              } else {
                reject(error.response.data.message);
              }
            });
        });
      }
    },
    getOrdersPageData(context, data) {
      if (context.getters.loggedIn) {
        return new Promise((resolve, reject) => {
          axios
            .get(rootAPI + "booking", {
              params: { page: data.page, time: data.type },
            })
            .then((response) => {
              if (response.data.data.data.length)
                resolve(response.data.data.data);
              else resolve("reached-maximum");
            })
            .catch((error) => {
              if (error.message == "Network Error" && !error.response) {
                reject("Please check your Internet connection");
              } else {
                reject(error.response.data.message);
              }
            });
        });
      }
    },
    initSocket({ state }) {
      if (state.socket.connected) return;

      /*
        Since watchers doesn't work probably on objects unless we use 'deep' argument
        assigning entirely new object to a property will trigger the watcher
        so below once the socket is connected the socket object will be assigned to the state
      */
      return new Promise((resolve, reject) => {
        const socket = io(`${rootSocket}/passengers`, {
          transportOptions: {
            polling: {
              extraHeaders: axios.defaults.headers.common,
            },
          },
        });

        socket.on("connect", () => {
          state.socket = socket;
          resolve(true);
        });

        socket.on("connect_error", () => {
          reject(false);
        });
      });
    },

    setLanguagePreference(context, language) {
      localStorage.setItem("selected_language", language);
      context.commit("setLanguage", language);
      if (context.getters.loggedIn) {
        return new Promise((resolve, reject) => {
          axios
            .post(rootAPI + "change-lang", {
              language: language,
            })
            .then((response) => {
              resolve(response);
            })
            .catch((error) => {
              if (error.message == "Network Error" && !error.response) {
                reject("Please check your Internet connection");
              }
              reject(error.response.data.message);
            });
        });
      }
    },

    getCancellationReasons({ commit, state }) {
      if (state.cancellationReasons.length > 0) {
        return state.cancellationReasons;
      }
      return new Promise((resolve, reject) => {
        axios
          .get(rootAPI + "cancellation-reasons")
          .then((res) => {
            commit("setCancellationReasons", res.data.data || []);
            resolve(res.data.data);
          })
          .catch((err) => {
            if (err.message == "Network Error" && !err.response) {
              reject("Please check your Internet connection");
            } else {
              reject(err.response.data.message);
            }
          });
      });
    },
  },
  modules: {},
});
